/**
 * i18next setup.
 *
 * Call `initI18n()` once before rendering translated UI (the root layout
 * does this). The user's preference is stored as `'system'` or a language
 * code; resolution and fallback rules live in `resolve-language.ts`.
 *
 * **Only English is bundled eagerly.** It is `fallbackLng`, so i18next needs it
 * whatever the user picked, and `TranslationKey` below is derived from its
 * shape. The other six languages are ~760 KB of JSON between them and are
 * loaded on demand — this module is on the boot graph (`api/client.ts` imports
 * it) and the root layout gates its first render on `initI18n()`, so anything
 * parsed here is parsed in front of the first pixel.
 *
 * The rule that keeps that from flashing untranslated UI: **a language is
 * loaded before i18next is told about it, never after.** `initI18n` loads the
 * resolved language before `init`, and every later switch goes through
 * `ensureLanguageLoaded` before `changeLanguage`.
 */

import { getLocales } from "expo-localization";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { AppState } from "react-native";

import { storage } from "@/lib/storage";

import {
  DEFAULT_LANGUAGE,
  isLanguageEnabled,
  isSupportedLanguage,
  type SupportedLanguage,
} from "./languages";
import { createLanguageLoader } from "./language-loading";
import { loadLanguageBundle } from "./load-language-bundle";
import {
  resolveAppLanguage,
  resolveSavedLanguage,
  type LanguagePreference,
} from "./resolve-language";
import en from "./resources/bundles/en";

const LANGUAGE_PREFERENCE_KEY = "opncue.languagePreference";

export const resources = {
  en: { translation: en },
} as const;

type DotPath<T> = {
  [Key in keyof T & string]: T[Key] extends string
    ? Key
    : T[Key] extends Record<string, unknown>
      ? `${Key}.${DotPath<T[Key]>}`
      : never;
}[keyof T & string];

type EnglishTranslationPath = DotPath<(typeof resources)["en"]["translation"]>;
type PluralTranslationBase = EnglishTranslationPath extends infer Path
  ? Path extends `${infer Base}_${"zero" | "one" | "two" | "few" | "many" | "other"}`
    ? Base
    : never
  : never;

/** Valid translation paths, including i18next plural bases, from English. */
export type TranslationKey = EnglishTranslationPath | PluralTranslationBase;

const languageLoader = createLanguageLoader({
  fallback: DEFAULT_LANGUAGE,
  isLoaded: (language) => i18n.hasResourceBundle(language, "translation"),
  load: (language) => loadLanguageBundle(language as SupportedLanguage),
  add: (language, bundle) => {
    i18n.addResourceBundle(language, "translation", bundle, true, true);
  },
});

/**
 * Make sure a language's catalogue is in memory. Resolves only once it is, so a
 * caller can hand the code straight to i18next without a window in which the
 * app renders a language it does not hold.
 */
async function ensureLanguageLoaded(language: string): Promise<void> {
  await languageLoader.ensure(language);
}

function deviceLanguageTag(): string | null {
  try {
    return getLocales()[0]?.languageTag ?? null;
  } catch {
    return null;
  }
}

export async function getLanguagePreference(): Promise<LanguagePreference> {
  const saved = await storage.get(LANGUAGE_PREFERENCE_KEY);
  if (isSupportedLanguage(saved) && isLanguageEnabled(saved)) {
    return saved;
  }
  // Missing, 'system', or a language that is unknown / no longer enabled all
  // behave as "follow the device".
  return "system";
}

export async function setLanguagePreference(
  preference: LanguagePreference,
): Promise<void> {
  await storage.set(LANGUAGE_PREFERENCE_KEY, preference);
  const next = resolveSavedLanguage(preference, deviceLanguageTag());
  if (i18n.language !== next) {
    await ensureLanguageLoaded(next);
    await i18n.changeLanguage(next);
  }
}

export function getCurrentAppLanguage(): SupportedLanguage {
  const current = i18n.resolvedLanguage ?? i18n.language;
  return isSupportedLanguage(current) && isLanguageEnabled(current)
    ? current
    : DEFAULT_LANGUAGE;
}

// Re-check the device locale when the app returns to the foreground, but only
// for users following the system language — a manual choice always wins.
// `getLocales()` is constant per run on iOS but not on Android, which is why
// this is a foreground check rather than a one-off read.
let watchingAppState = false;
function watchSystemLanguage(): void {
  if (watchingAppState) return;
  watchingAppState = true;
  AppState.addEventListener("change", (state) => {
    if (state !== "active") return;
    void (async () => {
      const preference = await getLanguagePreference();
      if (preference !== "system") return;
      const next = resolveAppLanguage(deviceLanguageTag());
      if (i18n.language !== next) {
        await ensureLanguageLoaded(next);
        await i18n.changeLanguage(next);
      }
    })();
  });
}

let initPromise: Promise<void> | null = null;

export function initI18n(): Promise<void> {
  if (!initPromise) {
    initPromise = (async () => {
      const saved = await storage.get(LANGUAGE_PREFERENCE_KEY);
      const initial = resolveSavedLanguage(saved, deviceLanguageTag());

      // Loaded before `init`, not added after it: the first render must already
      // hold the language it is about to draw.
      const bundle =
        initial === DEFAULT_LANGUAGE
          ? null
          : await loadLanguageBundle(initial as SupportedLanguage).catch(
              // A missing catalogue is a degraded app, not a broken one —
              // `fallbackLng` answers every key.
              () => null,
            );

      await i18n.use(initReactI18next).init({
        resources: bundle
          ? { ...resources, [initial]: { translation: bundle } }
          : resources,
        lng: initial,
        fallbackLng: DEFAULT_LANGUAGE,
        interpolation: {
          // React already escapes rendered strings.
          escapeValue: false,
        },
        returnNull: false,
      });
      watchSystemLanguage();
    })();
  }
  return initPromise;
}

export default i18n;
